import { AdMob } from "@capacitor-community/admob";
import { Capacitor } from "@capacitor/core";
import ADMOB from "config/ads";
import Logger from "utils/logger";

interface PlatformConfig {
  appId: string;
  bannerId: string;
  platform: string;
}

class AdmobService {
  private isInitialized: boolean = false;
  private isAdLoaded: boolean = false;
  private platform: string;
  private config: PlatformConfig;

  constructor() {
    this.platform = Capacitor.getPlatform();
    this.config = this.getPlatformConfig();
    this.setupListeners();
  }

  private getPlatformConfig(): PlatformConfig {
    const config = (ADMOB as any)[this.platform] || ADMOB.web;
    return config;
  }

  private setupListeners(): void {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    (AdMob as any).addListener("bannerAdLoaded", () => {
      this.isAdLoaded = true;
      Logger.debug("AdMob: Banner ad loaded successfully");
    });

    (AdMob as any).addListener("bannerAdFailedToLoad", (info: any) => {
      this.isAdLoaded = false;
      Logger.error(
        "AdMob: Banner ad failed to load:",
        JSON.stringify(info, null, 2)
      );
    });

    (AdMob as any).addListener("bannerAdSizeChanged", (info: any) => {
      Logger.debug(
        "AdMob: Banner ad size changed:",
        JSON.stringify(info, null, 2)
      );
    });

    (AdMob as any).addListener("bannerAdOpened", () => {
      Logger.debug("AdMob: Banner ad opened");
    });

    (AdMob as any).addListener("bannerAdClosed", () => {
      this.isAdLoaded = false;
      Logger.debug("AdMob: Banner ad closed");
    });

    (AdMob as any).addListener("bannerAdImpression", () => {
      Logger.debug("AdMob: Banner ad impression recorded");
    });
  }

  async initialize(): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    if (this.isInitialized) {
      return;
    }

    try {
      if (this.platform === "ios") {
        await AdMob.requestTrackingAuthorization();
      }

      await AdMob.initialize();
      this.isInitialized = true;
      Logger.debug("AdMob: Initialized successfully");
    } catch (error) {
      Logger.error("AdMob: Error during initialization:", error);
    }
  }

  async showBannerAd(): Promise<void> {
    if (!Capacitor.isNativePlatform() || !this.isInitialized) {
      return;
    }

    // Don't request a new banner if one is already loaded and visible
    if (this.isAdLoaded) {
      Logger.debug("AdMob: Banner ad already loaded, skipping show call");
      return;
    }

    try {
      await AdMob.showBanner({
        adId: this.config.bannerId,
        adSize: (Capacitor.getPlatform() === "ios"
          ? "BANNER"
          : "ADAPTIVE_BANNER") as any,
        position: "BOTTOM" as any,
        margin: Capacitor.getPlatform() === "ios" ? -20 : 0,
      } as any);
    } catch (error) {
      Logger.error("AdMob: Error showing banner ad:", error);
    }
  }

  async hideBannerAd(): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    try {
      await AdMob.removeBanner();
      this.isAdLoaded = false;
    } catch (error) {
      Logger.error("AdMob: Error hiding banner ad:", error);
    }
  }

  isReady(): boolean {
    return this.isInitialized;
  }
}

const admobService = new AdmobService();
export default admobService;
